// L3 — advergame state and mechanics (INTERACTION-MODEL-V1.md §4 and §7).
// Declared variables/timers/behaviors/templates only; no code.

import type { Action, Rule, RunnerEffect, RunnerObjectKind } from "../schema/document.js";
import { ENGINE_LIMITS } from "../limits.js";
import type { IssueSink } from "../issues.js";
import { BEHAVIOR_REGISTRY_V1 } from "../registry.js";
import { inLayout, rootOf, type DocIndex } from "./context.js";

function* allActions(actions: readonly Action[], path: string): Generator<[Action, string]> {
  for (const [i, a] of actions.entries()) {
    const p = `${path}[${String(i)}]`;
    yield [a, p];
    if (a.type === "after") yield* allActions(a.do, `${p}.do`);
  }
}

export function validateGame(index: DocIndex, sink: IssueSink): void {
  const doc = index.doc;

  doc.variables?.forEach((v, i) => {
    const p = `$.variables[${String(i)}]`;
    if (v.type === "number") {
      if (v.min !== undefined && v.max !== undefined && v.min > v.max)
        sink.add("VARIABLE_INITIAL_INVALID", p, "Variable minimum exceeds maximum.", {});
      if ((v.min !== undefined && v.initial < v.min) || (v.max !== undefined && v.initial > v.max))
        sink.add("VARIABLE_INITIAL_INVALID", `${p}.initial`, "Initial value is outside the declared range.", {});
      if (v.integer === true && !Number.isInteger(v.initial))
        sink.add("VARIABLE_INITIAL_INVALID", `${p}.initial`, "Integer variables start at an integer.", {});
    }
    if (v.type === "enum" && !v.values.includes(v.initial))
      sink.add("VARIABLE_INITIAL_INVALID", `${p}.initial`, "Initial value must be one of the declared values.", {});
  });

  // Behaviors.
  const colliderTags = new Set<string>();
  doc.elements.forEach((el, ei) => {
    const p = `$.elements[${String(ei)}]`;
    const types = new Set<string>();
    el.behaviors?.forEach((b, bi) => {
      const bp = `${p}.behaviors[${String(bi)}]`;
      const def = BEHAVIOR_REGISTRY_V1[b.type];
      if (!(def.appliesTo as readonly string[]).includes(el.type))
        sink.add("BEHAVIOR_INVALID", bp, `Behavior ${b.type} does not apply to ${el.type} elements.`, {
          behavior: b.type,
        });
      if (types.has(b.type))
        sink.add("BEHAVIOR_INVALID", bp, `Behavior ${b.type} is declared twice.`, { behavior: b.type });
      types.add(b.type);
      if (b.type === "move") {
        if ((b.velocity === undefined) === (b.path === undefined))
          sink.add("BEHAVIOR_INVALID", bp, "move declares exactly one of velocity or path.", {});
        if (b.path !== undefined && b.speed === undefined)
          sink.add("BEHAVIOR_INVALID", bp, "Path movement declares a speed.", {});
      }
      if (b.type === "draggable")
        b.dropTargets?.forEach((t, ti) => {
          if (!index.elements.has(t))
            sink.add("UNKNOWN_REFERENCE", `${bp}.dropTargets[${String(ti)}]`, `Unknown element "${t}".`, {
              kind: "element",
              id: t,
            });
        });
      if (b.type === "collider") for (const t of el.tags ?? []) colliderTags.add(t);
    });
    if (el.template === true) {
      const root = rootOf(index, el.id);
      if (root === undefined || root === "overlay")
        sink.add("TEMPLATE_INVALID", p, "Templates are placed in the scene where their instances spawn.", {});
    }
  });

  const hasCollider = (side: { readonly id?: string | undefined; readonly tag?: string | undefined }) =>
    side.id !== undefined
      ? (index.elements.get(side.id)?.value.behaviors?.some((b) => b.type === "collider") ?? false)
      : side.tag !== undefined && colliderTags.has(side.tag);

  (doc.rules ?? []).forEach((rule: Rule, ri) => {
    const rp = `$.rules[${String(ri)}]`;
    if (rule.on.type === "collision.enter" || rule.on.type === "collision.exit") {
      for (const side of ["a", "b"] as const) {
        const ref = rule.on[side];
        if (!hasCollider(ref))
          sink.add(
            "COLLIDER_MISSING",
            `${rp}.on.${side}`,
            `No element with a collider matches ${ref.id !== undefined ? `id "${ref.id}"` : `tag "${String(ref.tag)}"`}.`,
            {}
          );
      }
    }
    for (const [a, p] of allActions(rule.do, `${rp}.do`)) {
      if (a.type === "spawn") {
        const t = index.elements.get(a.template)?.value;
        if (t?.template !== true)
          sink.add("TEMPLATE_INVALID", `${p}.template`, `"${a.template}" is not a template element.`, {
            id: a.template,
          });
      }
      if (a.type === "despawn" && a.target.id !== undefined && index.elements.get(a.target.id)?.value.template !== true)
        sink.add("TEMPLATE_INVALID", `${p}.target.id`, "Only spawned instances (templates) can be despawned.", {
          id: a.target.id,
        });
    }
  });
}

// ─── Game module (7.6F, ADR-0022) ──────────────────────────────────────────
//
// Proves statically that a declared game can be played and can end: every
// mechanic element is a top-level layer of a game scene with exactly one role,
// pairs are well formed, drop items are draggable onto declared zones, the win
// condition has what it needs in EVERY format variant, results are scenes
// outside the game. Never repairs: reports.

type GameRole = "target" | "card" | "item" | "zone" | "player" | "control" | "course item" | "spawn template";

const TEXTUAL = new Set(["text", "button"]);

/** Frame of a top-level element in a variant (its override, else its base frame). */
function frameIn(index: DocIndex, layoutId: string, elementId: string) {
  const el = index.elements.get(elementId)?.value;
  return index.layouts.get(layoutId)?.value.overrides?.[elementId]?.frame ?? el?.frame;
}

function rulesEndGame(actions: readonly Action[]): boolean {
  return actions.some((a) => a.type === "game.end" || (a.type === "after" && rulesEndGame(a.do)));
}
function rulesStartGame(actions: readonly Action[]): boolean {
  return actions.some((a) => a.type === "game.start" || (a.type === "after" && rulesStartGame(a.do)));
}

export function validateGameModule(index: DocIndex, sink: IssueSink): void {
  const doc = index.doc;
  const game = doc.game;
  if (!game) return;
  const scenes = new Set(game.scenes);
  const roles = new Map<string, GameRole>();

  const claim = (id: string, role: GameRole, path: string): boolean => {
    const el = index.elements.get(id)?.value;
    if (!el) return false; // reported by the reference layer
    const prior = roles.get(id);
    if (prior !== undefined) {
      sink.add("GAME_ELEMENT_REUSED", path, `Element "${id}" already plays another role in the game (${prior}).`, {
        id,
        role: prior,
      });
      return false;
    }
    roles.set(id, role);
    const parent = index.parents.get(id);
    if (parent?.kind !== "scene" || !scenes.has(parent.id))
      sink.add("GAME_ELEMENT_OUTSIDE_SCENES", path, `Element "${id}" must be a top-level layer of a game scene.`, {
        id,
      });
    if (el.template === true)
      sink.add("GAME_ELEMENT_OUTSIDE_SCENES", path, `Template "${id}" cannot play a role in the game.`, { id });
    const hasText = TEXTUAL.has(el.type) || (el.type === "image" && el.alt.trim() !== "");
    if (el.interactive?.label === undefined && !hasText)
      sink.warn(
        "GAME_TARGET_UNLABELED",
        path,
        `Element "${id}" has no accessible label; keyboard and screen-reader players cannot tell what it is.`,
        { id }
      );
    return true;
  };

  // Variables, timer.
  const lives = game.lives !== undefined ? index.variables.get(game.lives)?.value : undefined;
  if (lives?.type === "number" && lives.initial < 1)
    sink.add("VARIABLE_INITIAL_INVALID", "$.game.lives", "Lives start at 1 or more.", { id: lives.id });
  const timer = game.timer !== undefined ? index.timers.get(game.timer)?.value : undefined;
  if (timer) {
    if (timer.repeat === true || (typeof timer.repeat === "number" && timer.repeat > 1))
      sink.add("GAME_SETUP_INVALID", "$.game.timer", "The time limit timer runs once (no repeat).", {
        id: timer.id,
      });
    if (timer.autoStart === true)
      sink.add("GAME_SETUP_INVALID", "$.game.timer", "The game starts its time limit; the timer cannot auto-start.", {
        id: timer.id,
      });
  }
  if (game.scoring.missCostsLife && game.lives === undefined)
    sink.add(
      "GAME_SETUP_INVALID",
      "$.game.scoring.missCostsLife",
      "Mistakes cost a life only when lives are declared.",
      {}
    );

  // Targets.
  game.targets?.forEach((t, i) => {
    const tp = `$.game.targets[${String(i)}]`;
    if (!claim(t.element, "target", `${tp}.element`)) return;
    const el = index.elements.get(t.element)?.value;
    if (t.role === "wrong" && (t.repeat !== undefined || t.foundState !== undefined))
      sink.add("GAME_TARGET_INVALID", tp, "Wrong targets always count each tap and have no found state.", {
        id: t.element,
      });
    if (t.repeat === true && t.foundState !== undefined)
      sink.add("GAME_TARGET_INVALID", `${tp}.foundState`, "Repeatable targets are never 'found'.", { id: t.element });
    if (t.foundState !== undefined && el && !Object.hasOwn(el.states ?? {}, t.foundState))
      sink.add("GAME_TARGET_INVALID", `${tp}.foundState`, `Element "${t.element}" has no state "${t.foundState}".`, {
        id: t.element,
      });
    if (t.relocate !== undefined) {
      if (t.relocate === t.element)
        sink.add("GAME_TARGET_INVALID", `${tp}.relocate`, "A target cannot be its own relocation area.", {
          id: t.element,
        });
      else if (index.parents.get(t.relocate)?.kind !== "scene")
        sink.add("GAME_TARGET_INVALID", `${tp}.relocate`, "The relocation area must be a top-level layer.", {
          id: t.relocate,
        });
    }
  });

  // Pairs.
  const pairs = game.pairs;
  if (pairs) {
    pairs.cards.forEach((pair, i) => {
      const pp = `$.game.pairs.cards[${String(i)}]`;
      const [a, b] = pair as readonly [string, string];
      if (a === b) {
        sink.add("GAME_PAIRS_INVALID", pp, `A pair needs two different elements ("${a}").`, { id: a });
        return;
      }
      const okA = claim(a, "card", `${pp}[0]`);
      const okB = claim(b, "card", `${pp}[1]`);
      if (!okA || !okB) return;
      for (const l of doc.layouts)
        if (inLayout(index, a, l.id) !== inLayout(index, b, l.id))
          sink.add("GAME_PAIRS_INVALID", pp, `Both cards of a pair exist in the same formats (layout "${l.id}").`, {
            layout: l.id,
          });
      if (pairs.matchedState !== undefined)
        for (const c of pair)
          if (!Object.hasOwn(index.elements.get(c)?.value.states ?? {}, pairs.matchedState))
            sink.add(
              "GAME_PAIRS_INVALID",
              "$.game.pairs.matchedState",
              `Card "${c}" has no state "${pairs.matchedState}".`,
              {
                id: c,
              }
            );
    });
    if (pairs.shuffle)
      for (const l of doc.layouts) {
        const sizes = new Set(
          pairs.cards
            .flat()
            .filter((c) => inLayout(index, c, l.id))
            .map((c) => {
              const f = frameIn(index, l.id, c);
              return f ? `${String(f.width)}x${String(f.height)}` : "";
            })
        );
        if (sizes.size > 1)
          sink.warn(
            "GAME_SHUFFLE_SIZES_DIFFER",
            "$.game.pairs.shuffle",
            `Shuffled cards have different sizes in layout "${l.id}"; each card takes the frame of the slot it lands in.`,
            { layout: l.id }
          );
      }
  }

  // Drops.
  const drops = game.drops;
  if (drops) {
    const zones = new Set(drops.zones);
    drops.zones.forEach((z, i) => claim(z, "zone", `$.game.drops.zones[${String(i)}]`));
    drops.items.forEach((it, i) => {
      const ip = `$.game.drops.items[${String(i)}]`;
      if (!claim(it, "item", ip)) return;
      const drag = index.elements.get(it)?.value.behaviors?.find((b) => b.type === "draggable");
      if (drag?.type !== "draggable" || (drag.dropTargets?.length ?? 0) === 0)
        sink.add("GAME_DROPS_INVALID", ip, `Item "${it}" needs a draggable behavior with its accepted zones.`, {
          id: it,
        });
      else
        for (const z of drag.dropTargets ?? [])
          if (!zones.has(z))
            sink.add("GAME_DROPS_INVALID", ip, `Item "${it}" accepts "${z}", which is not a drop zone of the game.`, {
              id: it,
              zone: z,
            });
    });
  }

  // Win condition: what it needs, in every variant.
  const objectives = [
    ...(game.targets ?? []).filter((t) => t.role === "correct" && t.repeat !== true).map((t) => t.element),
    ...(pairs?.cards.flat() ?? []),
    ...(drops?.items ?? []),
  ];
  const win = game.win;
  if (win.when === "cleared") {
    if (objectives.length === 0)
      sink.add(
        "GAME_WIN_INVALID",
        "$.game.win",
        "Clearing the game needs objectives (one-time targets, pairs or drop items).",
        {}
      );
    else
      for (const l of doc.layouts)
        if (!objectives.some((o) => inLayout(index, o, l.id)))
          sink.add("GAME_WIN_INVALID", "$.game.win", `Layout "${l.id}" has no objective to clear.`, { layout: l.id });
  }
  if (win.when === "score" && game.score === undefined)
    sink.add("GAME_WIN_INVALID", "$.game.win", "A score goal needs a score variable.", {});
  if (win.when === "survive" && game.timer === undefined)
    sink.add("GAME_WIN_INVALID", "$.game.win", "Surviving needs a time limit.", {});
  if (win.when === "finish" && !game.runner)
    sink.add("GAME_WIN_INVALID", "$.game.win", "Finishing the course needs a runner.", {});
  if (game.runner) validateRunner(index, sink, roles);

  // Results: scenes outside the game.
  for (const key of ["win", "lose", "timeout"] as const) {
    const sc = game.results[key];
    if (sc !== undefined && scenes.has(sc))
      sink.add("GAME_RESULTS_INVALID", `$.game.results.${key}`, `Result scene "${sc}" is a game scene.`, { scene: sc });
  }

  const rules = doc.rules ?? [];
  if (
    win.when === "manual" &&
    game.timer === undefined &&
    game.lives === undefined &&
    game.runner?.energy === undefined &&
    !rules.some((r) => rulesEndGame(r.do))
  )
    sink.warn(
      "GAME_NEVER_ENDS",
      "$.game",
      "Nothing ends this game: no win condition, time limit, lives or game.end rule.",
      {}
    );
  if (game.start === "action" && !rules.some((r) => rulesStartGame(r.do)))
    sink.warn("GAME_NEVER_STARTS", "$.game.start", "The game waits for a start action, but no rule starts it.", {});
}

// ─── Runner (7.6F.1, ADR-0023) ─────────────────────────────────────────────
//
// Proves statically that a runner can run in every format: the player and the
// course pieces are top-level layers of a game scene with one role each, spawn
// templates are template layers of a game scene present where they spawn, the
// course fits the engine's duration ceiling, the total of live instances is
// bounded, effects only use declared variables and have the sign of their
// kind, and the energy variable has a range. Never repairs: reports.

function validateRunner(index: DocIndex, sink: IssueSink, roles: Map<string, GameRole>): void {
  const doc = index.doc;
  const game = doc.game;
  const runner = game?.runner;
  if (!game || !runner) return;
  const scenes = new Set(game.scenes);
  const rp = "$.game.runner";
  const bad = (path: string, message: string, params: Record<string, string | number> = {}) => {
    sink.add("GAME_RUNNER_INVALID", path, message, params);
  };
  const inGameScene = (id: string) => {
    const parent = index.parents.get(id);
    return parent?.kind === "scene" && scenes.has(parent.id);
  };
  /** A layer with one exclusive runner role, top-level in a game scene (templates only as spawn templates). */
  const claim = (id: string, role: GameRole, path: string, template: boolean): boolean => {
    const el = index.elements.get(id)?.value;
    if (!el) return false; // reported by the reference layer
    const prior = roles.get(id);
    if (prior !== undefined) {
      sink.add("GAME_ELEMENT_REUSED", path, `Element "${id}" already plays another role in the game (${prior}).`, {
        id,
        role: prior,
      });
      return false;
    }
    roles.set(id, role);
    if (!inGameScene(id))
      sink.add("GAME_ELEMENT_OUTSIDE_SCENES", path, `Element "${id}" must be a top-level layer of a game scene.`, {
        id,
      });
    if ((el.template === true) !== template)
      bad(path, template ? `"${id}" is not a template layer.` : `Template "${id}" cannot play this role.`, { id });
    return true;
  };

  // Player.
  const player = runner.player;
  if (claim(player.element, "player", `${rp}.player.element`, false)) {
    const el = index.elements.get(player.element)?.value;
    for (const key of ["jump", "duck", "hit"] as const) {
      const st = player.states?.[key];
      if (st !== undefined && el && !Object.hasOwn(el.states ?? {}, st))
        bad(`${rp}.player.states.${key}`, `Player "${player.element}" has no state "${st}".`, { id: player.element });
    }
    for (const l of doc.layouts)
      if (!inLayout(index, player.element, l.id))
        bad(`${rp}.player.element`, `The player is missing from layout "${l.id}".`, { layout: l.id });
  }
  if (player.states?.duck !== undefined && !player.duck)
    bad(`${rp}.player.states.duck`, "A duck pose needs the duck move.", {});

  // Controls (keyboard always works; touch players need on-screen controls).
  for (const key of ["jump", "duck"] as const) {
    const id = runner.controls?.[key];
    if (id === undefined) continue;
    claim(id, "control", `${rp}.controls.${key}`, false);
    if (key === "duck" && !player.duck) bad(`${rp}.controls.duck`, "A duck control needs the duck move.", {});
  }
  if (runner.controls?.jump === undefined)
    sink.warn(
      "GAME_RUNNER_NO_TOUCH_CONTROLS",
      `${rp}.controls`,
      "No on-screen jump control: players on touch screens cannot jump (the keyboard still works).",
      {}
    );

  // Layers (parallax) and segment scenery: top-level layers of a game scene, never the player or a piece.
  runner.layers?.forEach((l, i) => {
    const lp = `${rp}.layers[${String(i)}]`;
    const el = index.elements.get(l.element)?.value;
    if (!el) return;
    if (roles.has(l.element) || !inGameScene(l.element) || el.template === true)
      bad(`${lp}.element`, `"${l.element}" cannot be a moving background layer.`, { id: l.element });
    if (runner.layers?.findIndex((x) => x.element === l.element) !== i)
      bad(`${lp}.element`, `Layer "${l.element}" is declared twice.`, { id: l.element });
    if (l.loop)
      for (const lay of doc.layouts) {
        if (!inLayout(index, l.element, lay.id)) continue;
        const f = frameIn(index, lay.id, l.element);
        const width = lay.sizing.mode === "fixed" ? lay.sizing.width : lay.sizing.design.width;
        if (f && f.width < width)
          sink.warn(
            "GAME_RUNNER_LAYER_GAP",
            `${lp}.loop`,
            `Looping layer "${l.element}" is narrower than layout "${lay.id}": gaps will show between repeats.`,
            { id: l.element, layout: lay.id }
          );
      }
  });
  const segmentIds = new Set<string>();
  let course = 0;
  runner.segments.forEach((sg, i) => {
    const sp = `${rp}.segments[${String(i)}]`;
    if (segmentIds.has(sg.id)) bad(`${sp}.id`, `Segment "${sg.id}" is declared twice.`, { id: sg.id });
    segmentIds.add(sg.id);
    course += sg.durationMs;
    sg.show.forEach((id, j) => {
      const el = index.elements.get(id)?.value;
      if (!el) return;
      if (roles.has(id) || !inGameScene(id) || el.template === true)
        bad(`${sp}.show[${String(j)}]`, `"${id}" cannot be scenery of a segment.`, { id });
    });
  });
  if (course > ENGINE_LIMITS.durationMaxMs)
    bad(`${rp}.segments`, `The course lasts ${String(course)} ms; at most ${String(ENGINE_LIMITS.durationMaxMs)}.`, {
      max: ENGINE_LIMITS.durationMaxMs,
    });

  // Effects: declared variables, and the sign of their kind.
  const effect = (e: RunnerEffect, kind: RunnerObjectKind, path: string) => {
    const values = [e.energy, e.score, e.lives].filter((v): v is number => v !== undefined);
    if (kind === "pickup" ? !values.some((v) => v > 0) || values.some((v) => v < 0) : values.some((v) => v > 0))
      bad(
        path,
        kind === "pickup"
          ? "A pickup gives something (energy, points or a life) and takes nothing."
          : `A ${kind} only takes (energy, points or lives).`,
        { kind }
      );
    if (e.energy !== undefined && !runner.energy) bad(`${path}.energy`, "Energy effects need the runner's energy.", {});
    if (e.lives !== undefined && game.lives === undefined) bad(`${path}.lives`, "Life effects need lives.", {});
    if (e.score !== undefined && game.score === undefined) bad(`${path}.score`, "Point effects need a score.", {});
  };

  // Placed course items.
  runner.items?.forEach((it, i) => {
    const ip = `${rp}.items[${String(i)}]`;
    claim(it.element, "course item", `${ip}.element`, false);
    effect(it.effect, it.kind, `${ip}.effect`);
  });

  // Spawn rules.
  const spawnIds = new Set<string>();
  let active = 0;
  runner.spawns?.forEach((sp, i) => {
    const p = `${rp}.spawns[${String(i)}]`;
    if (spawnIds.has(sp.id)) bad(`${p}.id`, `Spawn rule "${sp.id}" is declared twice.`, { id: sp.id });
    spawnIds.add(sp.id);
    active += sp.maxActive;
    if (sp.everyMs.min > sp.everyMs.max) bad(`${p}.everyMs`, "The shortest interval exceeds the longest.", {});
    if (sp.startMs !== undefined && sp.endMs !== undefined && sp.endMs <= sp.startMs)
      bad(`${p}.endMs`, "The spawn window ends before it starts.", {});
    sp.segments?.forEach((sg, j) => {
      if (!runner.segments.some((x) => x.id === sg))
        sink.add("UNKNOWN_REFERENCE", `${p}.segments[${String(j)}]`, `Unknown runner segment "${sg}".`, {
          kind: "segment",
          id: sg,
        });
    });
    sp.templates.forEach((t, j) => {
      if (!claim(t, "spawn template", `${p}.templates[${String(j)}]`, true)) return;
      if (!doc.layouts.some((l) => inLayout(index, t, l.id)))
        bad(`${p}.templates[${String(j)}]`, `Template "${t}" is in no format.`, { id: t });
    });
    effect(sp.effect, sp.kind, `${p}.effect`);
  });
  if (active > ENGINE_LIMITS.runnerActiveMax)
    bad(
      `${rp}.spawns`,
      `Up to ${String(active)} objects could be alive at once; at most ${String(ENGINE_LIMITS.runnerActiveMax)}.`,
      { max: ENGINE_LIMITS.runnerActiveMax }
    );

  // Energy: a declared number with a range, starting above its minimum.
  const energy = runner.energy ? index.variables.get(runner.energy.var)?.value : undefined;
  if (energy?.type === "number") {
    if (energy.min === undefined || energy.max === undefined)
      bad(`${rp}.energy.var`, "Energy needs a minimum and a maximum.", { id: energy.id });
    else if (energy.initial <= energy.min)
      bad(`${rp}.energy.var`, "Energy starts above its minimum.", { id: energy.id });
  }
}
